import type React from 'react';
import { createContext, useContext, useMemo } from 'react';

import deStrings from '../../content/ui-strings/de.json';
import enStrings from '../../content/ui-strings/en.json';

export interface LanguageContextValue {
  lang: 'de' | 'en';
  alternateSlug: string;
  t: (key: string) => string;
}

const uiStrings: Record<'de' | 'en', Record<string, unknown>> = {
  de: deStrings,
  en: enStrings,
};

/**
 * Resolves a dot-notation key (e.g. "nav.home") against a nested object.
 * Returns the key itself if the path cannot be resolved to a string value.
 */
export function resolveTranslation(strings: Record<string, unknown>, key: string): string {
  const parts = key.split('.');
  let current: unknown = strings;

  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return key;
    }
    current = (current as Record<string, unknown>)[part];
  }

  if (typeof current === 'string') {
    return current;
  }

  return key;
}

const defaultValue: LanguageContextValue = {
  lang: 'de',
  alternateSlug: '/en/',
  t: (key: string) => resolveTranslation(uiStrings.de, key),
};

const LanguageContext = createContext<LanguageContextValue>(defaultValue);

export interface LanguageProviderProps {
  lang: 'de' | 'en';
  alternateSlug: string;
  children: React.ReactNode;
}

export function LanguageProvider({ lang, alternateSlug, children }: LanguageProviderProps) {
  const value = useMemo<LanguageContextValue>(() => {
    const strings = uiStrings[lang];
    return {
      lang,
      alternateSlug,
      t: (key: string) => resolveTranslation(strings, key),
    };
  }, [lang, alternateSlug]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext);
}

export default LanguageContext;
